import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import { ROUTES } from "@/constants/routes";
import { useClients } from "@/lib/hooks/useClients";
import PageLayout from "@/pages/page-layout/PageLayout";
import { BreadcrumbType } from "@/types/breadcrumb-types";

interface ClientTabsLayoutProps {
  children: React.ReactNode;
  clientId: string;
  currentPage?: BreadcrumbType["currentPage"];
  navigation?: BreadcrumbType["navigation"];
  header?: string;
}

const ClientTabsLayoutContent = ({
  children,
  clientId,
  currentPage,
  navigation,
  header,
}: ClientTabsLayoutProps) => {
  const { client, clientError, clientLoading } = useClients();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const breadcrumb = {
    currentPage: currentPage || {
      label: location.state?.clientName || client?.name,
      url: null,
    },
    navigation: [
      {
        label: t("common.clients"),
        url: `/${ROUTES.clients}`,
      },
      ...(currentPage
        ? [
          {
            label: location.state?.clientName || client?.name || "Undefined",
            url: null,
          },
        ]
        : []),
    ],
  };

  if (clientLoading && !clientError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Loader2 className="size-16 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (clientError?.status === 404 || !client) {
    return (
      <Navigate to={`/${ROUTES.clients}`} replace state={{ status: 404 }} />
    );
  }

  return (
    <PageLayout
      breadcrumb={{
        ...breadcrumb,
        navigation: [...breadcrumb.navigation, ...(navigation || [])],
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {header || client?.name || String(location.state?.clientName)}
          </CardTitle>
          {!header && (
            <CardDescription>{t("admin.client.subheader")}</CardDescription>
          )}
          <CardAction>
            <Button
              variant="link"
              onClick={() => navigate(`/${ROUTES.clients}/${clientId}/${ROUTES.edit}`)}
            >
              <Icons.EditSquareIcon fontSize={24} />
              {t("admin.client.editClient")}
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </PageLayout>
  );
};

const ClientTabsLayout = (props: ClientTabsLayoutProps) => {
  if (!props.clientId) return null;

  return <ClientTabsLayoutContent {...props} clientId={props.clientId} />;
};

export default ClientTabsLayout;
